import { getSupabaseClient } from '@/lib/auth/client'
import { clientLogger } from '@/lib/client-logger'

const log = clientLogger('UserProfile')

export interface UserProfile {
  id: string
  email: string
  firstName?: string
  lastName?: string
  fullName?: string
}

export interface UserProfileService {
  getCurrentUserProfile: () => Promise<UserProfile | null>
}

class UserProfileServiceImpl implements UserProfileService {

  async getCurrentUserProfile(): Promise<UserProfile | null> {
    try {
      // Get current authenticated user
      const supabase = getSupabaseClient()
      const { data: { user }, error: authError } = await supabase.auth.getUser()
      
      if (authError || !user) {
        // Signed-out is a STATE, not a failure. getUser() rejects with
        // AuthSessionMissingError on every public page (e.g. /auth), so
        // logging it at error level buries real errors under expected noise —
        // every signed-out visitor generated 4+ console errors (one per
        // component mounting useUserProfile). Only a failure that is NOT
        // "there is simply no session" earns the red.
        if (authError && authError.name !== 'AuthSessionMissingError') {
          log.error('Auth error:', authError)
        }
        return null
      }

      // Build basic profile from auth user
      const profile: UserProfile = {
        id: user.id,
        email: user.email || '',
        firstName: undefined,
        lastName: undefined,
        fullName: undefined
      }

      // Try to get profile from people table using user_id first
      try {
        let personData = null
        let personError = null

        // First try: lookup by auth_user_id
        const userIdResult = await supabase
          .from('people')
          .select('first_name, last_name')
          .eq('auth_user_id', user.id)
          .single()

        // Handle "not found" errors (PGRST116 or 406) with email fallback
        const isNotFound = userIdResult.error && 
          (userIdResult.error.code === 'PGRST116' || 
           userIdResult.error.code === '406' || 
           userIdResult.error.message?.includes('406'))

        if (isNotFound) {
          // Silently try fallback lookup by email (this is expected behavior)
          if (user.email) {
            const emailResult = await supabase
              .from('people')
              .select('first_name, last_name')
              .eq('email', user.email)
              .single()
            
            personData = emailResult.data
            personError = emailResult.error
          }
        } else {
          personData = userIdResult.data
          personError = userIdResult.error
        }

        if (personError) {
          const isStillNotFound = personError.code === 'PGRST116' || 
            personError.code === '406' || 
            personError.message?.includes('406')
          
          if (!isStillNotFound) {
            // Only log actual errors, not "not found" responses
            log.error('Error fetching person data:', personError)
          }
        } else if (personData) {
          // Successfully got person data
          profile.firstName = personData.first_name || undefined
          profile.lastName = personData.last_name || undefined
          profile.fullName = personData.first_name && personData.last_name 
            ? `${personData.first_name} ${personData.last_name}` 
            : undefined
        }
      } catch (peopleError) {
        log.warn('Failed to fetch people data, using basic auth profile:', peopleError)
      }

      return profile
    } catch (error) {
      log.error('Error in getCurrentUserProfile:', error)
      return null
    }
  }
}

// Export singleton instance
export const userProfileService = new UserProfileServiceImpl()

// Export utility functions for direct use
export const getCurrentUserProfile = () => userProfileService.getCurrentUserProfile()
